"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import styles from "./page.module.css";
import {
  offCampusData,
  onCampusData,
  importantThings,
  helpSectionContent,
} from "./data";
import type { AccommodationItem } from "./types";

import HeroSection from "./components/HeroSection/HeroSection";
import AccommodationIntro from "./components/AccommodationIntro/AccommodationIntro";
import AccommodationOptionsSection from "./components/AccommodationOptionsSection/AccommodationOptionsSection";
import AccommodationModal from "./components/AccommodationModal/AccommodationModal";
import ImportantThingsSection from "./components/ImportantThingsSection/ImportantThingsSection";
import HelpSection from "./components/HelpSection/HelpSection";
import Tabs, { TabItem } from "@/components/ui/Tabs/Tabs";

export default function AccommodationGuidesPage() {
  const t = useTranslations("AccommodationPage");

  const [selectedAccommodation, setSelectedAccommodation] =
    useState<AccommodationItem | null>(null);

  const translateItem = (item: AccommodationItem): AccommodationItem => {
    const key = `items.${item.id}` as const;
    return {
      ...item,
      about: t(`${key}.about`),
      priceNote: t(`${key}.priceNote`),
      pros: t.raw(`${key}.pros`) as string[],
      cons: t.raw(`${key}.cons`) as string[],
    };
  };

  const offCampusTranslated = useMemo(
    () => offCampusData.options.map(translateItem),
    [t],
  );
  const onCampusTranslated = useMemo(
    () => onCampusData.options.map(translateItem),
    [t],
  );

  const handleViewDetail = (item: AccommodationItem) => {
    setSelectedAccommodation(item);
  };

  const handleCloseModal = () => {
    setSelectedAccommodation(null);
  };

  const tabsData: TabItem[] = useMemo(
    () => [
      {
        id: "off-campus",
        label: t("tabs.offCampus"),
        content: (
          <div className={styles.tabContentSwap}>
            <AccommodationIntro
              title={t("tabs.offCampus")}
              description={t("offCampusDescription")}
            />
            <AccommodationOptionsSection
              items={offCampusTranslated}
              onViewDetail={handleViewDetail}
              sectionTitle={t("optionsTitle")}
              viewDetailLabel={t("viewDetail")}
            />
          </div>
        ),
      },
      {
        id: "on-campus",
        label: t("tabs.onCampus"),
        content: (
          <div className={styles.tabContentSwap}>
            <AccommodationIntro
              title={t("tabs.onCampus")}
              description={t("onCampusDescription")}
            />
            <AccommodationOptionsSection
              items={onCampusTranslated}
              onViewDetail={handleViewDetail}
              sectionTitle={t("optionsTitle")}
              viewDetailLabel={t("viewDetail")}
            />
          </div>
        ),
      },
    ],
    [t, offCampusTranslated, onCampusTranslated],
  );

  const translatedImportantThings = [
    {
      ...importantThings[0],
      title: t("importantThings.agents.title"),
      points: t.raw("importantThings.agents.points") as string[],
    },
    {
      ...importantThings[1],
      title: t("importantThings.housing.title"),
      points: t.raw("importantThings.housing.points") as string[],
    },
    {
      ...importantThings[2],
      title: t("importantThings.tips.title"),
      points: t.raw("importantThings.tips.points") as string[],
    },
  ];

  const modalLabels = {
    about: t("modal.about"),
    pros: t("modal.pros"),
    cons: t("modal.cons"),
    location: t("modal.location"),
    openMaps: t("modal.openMaps"),
  };

  const helpContacts = useMemo(() => {
    try {
      const rawContacts = t.raw("help.contacts");
      if (Array.isArray(rawContacts) && rawContacts.length > 0) {
        return rawContacts;
      }
    } catch {
      // Fallback to data.ts
    }
    return helpSectionContent.contacts;
  }, [t]);

  return (
    <main className={styles.page}>
      <HeroSection title={t("hero.title")} />

      <div className={styles.content}>
        <div className={styles.tabContainer}>
          <Tabs
            tabs={tabsData}
            defaultTab="off-campus"
            ariaLabel="Accommodation options navigation"
            className={styles.accommodationTab}
            reducedBottomSpacing
          />
        </div>

        <ImportantThingsSection
          items={translatedImportantThings}
          sectionTitle={t("importantTitle")}
        />

        <HelpSection
          title={t("help.title")}
          buttonText={t("help.button")}
          modalTitle={t("help.modalTitle")}
          contactsLabel={t("help.contactsLabel")}
          contacts={helpContacts}
        />
      </div>

      <AccommodationModal
        item={selectedAccommodation}
        onClose={handleCloseModal}
        labels={modalLabels}
      />
    </main>
  );
}
